import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import apiService from '../services/api.service';
import { cn } from '../lib/utils';

/**
 * Revenus LogFit — tout vient du grand livre Stripe : commission perçue,
 * frais Stripe réel, transfert à l'organisateur, frais de compte, virements.
 * Rien d'estimé, rien de recalculé ; la base ne fait que ranger par
 * événement. Ce que LogFit gagne = commissions − tous les frais Stripe, et
 * c'est le solde Stripe tant qu'aucun virement n'a été fait.
 */

interface Bucket {
  payments_count: number;
  collected_cents: number;
  paid_to_organizer_cents: number;
  commission_cents: number;
  stripe_fee_cents: number;
  net_cents: number;
}

interface Totals extends Bucket {
  account_fees_cents: number;
  account_fee_lines: number;
  net_total_cents: number;
  payouts_cents: number;
  payout_count: number;
}

interface EventRevenue extends Bucket {
  event_id: string;
  name: string;
  date_start: string | null;
  organization: string | null;
}

interface TokenRevenue extends Bucket {
  organization_id: string;
  organization: string | null;
}

interface RevenueResponse {
  totals: Totals;
  events: EventRevenue[];
  tokens: TokenRevenue[];
  unmatched: Bucket;
  stripe: {
    available_cents: number;
    pending_cents: number;
    ledger_net_cents: number;
    other_cents: number;
    other_types: Record<string, number>;
    unattributed_cents: number;
    read_at: string;
    live: boolean;
  };
}

const AdminRevenue: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState<RevenueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const euros = useMemo(
    () => new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }),
    [i18n.language],
  );
  const money = useCallback((cents: number) => euros.format(cents / 100), [euros]);
  const dateShort = useCallback((iso: string | null) => (
    iso ? new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short', year: 'numeric' }) : ''
  ), [i18n.language]);
  const timeShort = useCallback((iso: string) => (
    new Date(iso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })
  ), [i18n.language]);

  useEffect(() => {
    (async () => {
      try {
        setData(await apiService.getAdminRevenue());
      } catch (err: any) {
        setError(err.response?.data?.detail || t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const exportCSV = () => {
    if (!data) return;
    const headers = [
      t('admin.revenue.event'), t('admin.revenue.organization'), t('admin.revenue.date'),
      t('admin.revenue.payments'), t('admin.revenue.collected'), t('admin.revenue.paidToOrganizer'),
      t('admin.revenue.commission'), t('admin.revenue.stripeFees'), t('admin.revenue.net'),
    ];
    const line = (label: string, org: string, date: string, b: Bucket) => [
      label, org, date, b.payments_count,
      (b.collected_cents / 100).toFixed(2), (b.paid_to_organizer_cents / 100).toFixed(2),
      (b.commission_cents / 100).toFixed(2), (b.stripe_fee_cents / 100).toFixed(2), (b.net_cents / 100).toFixed(2),
    ];
    const { totals } = data;
    const rows = [
      ...data.events.map((e) => line(e.name, e.organization || '', e.date_start || '', e)),
      ...data.tokens.map((x) => line(t('admin.revenue.tokensLine'), x.organization || '', '', x)),
      ...(data.unmatched.payments_count ? [line(t('admin.revenue.unmatched'), '', '', data.unmatched)] : []),
      [t('admin.revenue.accountFeesRow', { count: totals.account_fee_lines }), '', '', '', '', '', '', (totals.account_fees_cents / 100).toFixed(2), (-totals.account_fees_cents / 100).toFixed(2)],
      [t('admin.revenue.total'), '', '', totals.payments_count, (totals.collected_cents / 100).toFixed(2), (totals.paid_to_organizer_cents / 100).toFixed(2), (totals.commission_cents / 100).toFixed(2), ((totals.stripe_fee_cents + totals.account_fees_cents) / 100).toFixed(2), (totals.net_total_cents / 100).toFixed(2)],
    ];
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const url = URL.createObjectURL(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'logfit-revenus.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-muted-foreground" /></div>;
  }
  if (error || !data) {
    return (
      <div className="p-4 md:p-6">
        <Alert variant="destructive"><AlertDescription>{error || t('common.error')}</AlertDescription></Alert>
      </div>
    );
  }

  const { totals, stripe } = data;
  const ledgerMatches = stripe.ledger_net_cents === stripe.available_cents + stripe.pending_cents;
  const th = 'pb-2 pr-4 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground';
  const num = 'pr-4 py-2.5 text-right tabular-nums whitespace-nowrap';
  const label = 'text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground';

  const Row: React.FC<{ label: React.ReactNode; b: Bucket; showOrganizer?: boolean; muted?: boolean }> = ({ label: l, b, showOrganizer = true, muted }) => (
    <tr className="border-b border-border">
      <td className={cn('py-2.5 pr-4 align-top', muted && 'text-muted-foreground')}>{l}</td>
      <td className={cn(num, 'text-muted-foreground')}>{b.payments_count}</td>
      <td className={num}>{money(b.collected_cents)}</td>
      {showOrganizer && <td className={cn(num, 'text-muted-foreground')}>{money(b.paid_to_organizer_cents)}</td>}
      <td className={num}>{money(b.commission_cents)}</td>
      <td className={num}>−{money(b.stripe_fee_cents)}</td>
      <td className={cn(num, 'font-semibold')}>{money(b.net_cents)}</td>
    </tr>
  );

  const Head: React.FC<{ showOrganizer?: boolean }> = ({ showOrganizer = true }) => (
    <thead>
      <tr className="border-b border-border text-left">
        <th className={th}>{t('admin.revenue.event')}</th>
        <th className={cn(th, 'text-right')}>{t('admin.revenue.payments')}</th>
        <th className={cn(th, 'text-right')}>{t('admin.revenue.collected')}</th>
        {showOrganizer && <th className={cn(th, 'text-right')}>{t('admin.revenue.paidToOrganizer')}</th>}
        <th className={cn(th, 'text-right')}>{t('admin.revenue.commission')}</th>
        <th className={cn(th, 'text-right')}>{t('admin.revenue.stripeFees')}</th>
        <th className={cn(th, 'text-right')}>{t('admin.revenue.net')}</th>
      </tr>
    </thead>
  );

  return (
    <div className="p-4 md:p-6 space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('admin.revenue.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('admin.revenue.subtitle')} · {t('admin.revenue.readAt', { time: timeShort(stripe.read_at) })}
            {!stripe.live && ` · ${t('admin.revenue.stripeTestMode')}`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV}>
          <Download size={16} className="mr-1" />CSV
        </Button>
      </div>

      {/* Chiffres de tête — sections ouvertes, pas de cartes */}
      <section className="grid grid-cols-2 gap-x-8 gap-y-6 border-b border-border pb-6 md:grid-cols-4">
        <div>
          <p className={label}>{t('admin.revenue.net')}</p>
          <p className="mt-1 text-3xl font-bold tabular-nums">{money(totals.net_total_cents)}</p>
          <p className="text-xs text-muted-foreground">{t('admin.revenue.netAfterAllFees')}</p>
        </div>
        <div>
          <p className={label}>{t('admin.revenue.stripeBalance')}</p>
          <p className="mt-1 text-xl font-semibold tabular-nums">{money(stripe.available_cents)}</p>
          <p className="text-xs text-muted-foreground">
            {totals.payouts_cents > 0
              ? t('admin.revenue.payoutsDone', { amount: money(totals.payouts_cents), count: totals.payout_count })
              : t('admin.revenue.noPayoutYet')}
            {stripe.pending_cents > 0 && ` · ${t('admin.revenue.stripePending', { amount: money(stripe.pending_cents) })}`}
          </p>
        </div>
        <div>
          <p className={label}>{t('admin.revenue.commission')}</p>
          <p className="mt-1 text-xl font-semibold tabular-nums">{money(totals.commission_cents)}</p>
          <p className="text-xs text-muted-foreground">{t('admin.revenue.commissionOn', { count: totals.payments_count, amount: money(totals.collected_cents) })}</p>
        </div>
        <div>
          <p className={label}>{t('admin.revenue.stripeFees')}</p>
          <p className="mt-1 text-xl font-semibold tabular-nums">−{money(totals.stripe_fee_cents + totals.account_fees_cents)}</p>
          <p className="text-xs text-muted-foreground">
            {t('admin.revenue.feesSplit', { payments: money(totals.stripe_fee_cents), account: money(totals.account_fees_cents) })}
          </p>
        </div>
      </section>

      {!ledgerMatches && (
        <Alert>
          <AlertDescription>
            {t('admin.revenue.ledgerMismatch', { ledger: money(stripe.ledger_net_cents), balance: money(stripe.available_cents + stripe.pending_cents) })}
          </AlertDescription>
        </Alert>
      )}

      {/* Par événement */}
      <section>
        <h2 className={cn(label, 'mb-3')}>{t('admin.revenue.byEvent')}</h2>
        {data.events.length === 0 && data.unmatched.payments_count === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin.revenue.noData')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <Head />
              <tbody>
                {data.events.map((e) => (
                  <Row key={e.event_id} b={e} label={
                    <div>
                      <div className="font-medium">{e.name}</div>
                      <div className="text-xs text-muted-foreground">{[e.organization, dateShort(e.date_start)].filter(Boolean).join(' · ')}</div>
                    </div>
                  } />
                ))}
                {data.unmatched.payments_count > 0 && (
                  <Row muted b={data.unmatched} label={t('admin.revenue.unmatched')} />
                )}
                {data.tokens.map((x) => (
                  <Row key={x.organization_id} b={x} label={
                    <div>
                      <div className="font-medium">{t('admin.revenue.tokensLine')}</div>
                      <div className="text-xs text-muted-foreground">{x.organization || x.organization_id}</div>
                    </div>
                  } />
                ))}
                <tr className="border-b border-border">
                  <td className="py-2.5 pr-4 text-muted-foreground">{t('admin.revenue.accountFeesRow', { count: totals.account_fee_lines })}</td>
                  <td className={num} /><td className={num} /><td className={num} /><td className={num} />
                  <td className={num}>−{money(totals.account_fees_cents)}</td>
                  <td className={cn(num, 'font-semibold')}>−{money(totals.account_fees_cents)}</td>
                </tr>
                <tr className="border-b border-border border-t-2 border-t-border font-semibold">
                  <td className="py-2.5 pr-4">{t('admin.revenue.total')}</td>
                  <td className={cn(num, 'text-muted-foreground')}>{totals.payments_count}</td>
                  <td className={num}>{money(totals.collected_cents)}</td>
                  <td className={cn(num, 'text-muted-foreground')}>{money(totals.paid_to_organizer_cents)}</td>
                  <td className={num}>{money(totals.commission_cents)}</td>
                  <td className={num}>−{money(totals.stripe_fee_cents + totals.account_fees_cents)}</td>
                  <td className={num}>{money(totals.net_total_cents)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminRevenue;
